import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSpinner, FaLock, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const formatPrice = (price) => {
    if (typeof price !== 'number') {
        return 'N/A';
    }
    return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0,
    }).format(price);
};

const CourseEnroll = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, processing, completed
    const { fetchUserCourses } = useAuth();

    useEffect(() => {
        const fetchCourseDetails = async () => {
            try {
                const response = await api.get(`/courses/${id}`);
                setCourse(response.data);
                setLoading(false);
            } catch (err) {
                setError('Failed to load course details');
                setLoading(false);
            }
        };
        fetchCourseDetails();
    }, [id]);

    const handleEnroll = async () => {
        try {
            setPaymentStatus('processing');
            // Call the real enroll API
            const token = localStorage.getItem('token');
            await api.post(
                `/courses/${id}/enroll`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPaymentStatus('completed');
            toast.success('Enrollment successful! Redirecting to course content...');
            await fetchUserCourses(); // Refresh enrolled courses globally
            setTimeout(() => {
                navigate(`/courses/${id}/content`);
            }, 2000); // Increased timeout slightly for toast visibility
        } catch (err) {
            console.error('Enroll error:', err.response?.data || err.message);
            const errorMessage = err.response?.data?.message || 'Enrollment failed. Please try again.';
            setError(errorMessage);
            toast.error(errorMessage);
            setPaymentStatus('pending');
        }
    };

    if (loading) {
        return (
            <div className="container py-5 text-center">
                <FaSpinner className="fa-spin fa-3x text-primary mb-3" />
                <h4>Loading course details...</h4>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5 text-center">
                <h4 className="text-danger">{error}</h4>
                <button 
                    className="btn btn-primary mt-3"
                    onClick={() => navigate('/courses')}
                >
                    Back to Courses
                </button>
            </div>
        );
    }

    if (!course) {
        return <div>No course found.</div>;
    }

    return (
        <div className="container py-5" style={{ marginTop: '75px' }}>
            <div className="row justify-content-center">
                <div className="col-md-8">
                    <div className="card shadow-lg border-0 rounded-lg">
                        <div className="card-header bg-primary text-white">
                            <h3 className="mb-0">Course Enrollment</h3>
                        </div>
                        <div className="card-body p-4">
                            <div className="course-details mb-4">
                                <h4 className="mb-3">{course.title}</h4>
                                <p className="text-muted">{course.description}</p>
                                <div className="row mt-4">
                                    <div className="col-md-6">
                                        <p><strong>Instructor:</strong> {course.instructor && typeof course.instructor === 'object' ? course.instructor.name : course.instructor}</p>
                                        <p><strong>Duration:</strong> {course.duration}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <p><strong>Level:</strong> {course.level}</p>
                                        <p><strong>Price:</strong> {formatPrice(course.price)}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="payment-section">
                                <h5 className="mb-3">Payment Details</h5>
                                <div className="alert alert-info">
                                    <FaLock className="me-2" />
                                    Secure payment processing
                                </div>
                                
                                {paymentStatus === 'completed' ? (
                                    <div className="alert alert-success">
                                        <FaCheckCircle className="me-2" />
                                        Payment successful! Redirecting to course...
                                    </div>
                                ) : (
                                    <button
                                        className="btn btn-primary btn-lg w-100"
                                        onClick={handleEnroll}
                                        disabled={paymentStatus === 'processing'}
                                    >
                                        {paymentStatus === 'processing' ? (
                                            <>
                                                <FaSpinner className="fa-spin me-2" />
                                                Processing Payment...
                                            </>
                                        ) : (
                                            `Pay ${formatPrice(course.price)}`
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseEnroll; 